import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import {  CheckCircle, ChevronRight, LogOut, Mail, Phone, Settings, Ticket, User } from 'lucide-react';
import { useState } from 'react';
import { AccountLayout } from './shared/AccountLayout';
import { LoginRequiredCard } from './LoginRequiredCard';
import { LogoutModal } from './LogoutModal';

interface MenuItemProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  showChevron?: boolean;
}

const MenuItem = ({ icon, title, subtitle, onClick, showChevron = true }: MenuItemProps) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 hover:bg-[#F7F8FA] transition-colors group"
    >
      <div className="w-10 h-10 rounded-full bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] group-hover:bg-[#FFF5F0] group-hover:text-[#FF6A2A] transition-colors">
        {icon}
      </div>
      <div className="flex-1 text-left">
        <p className="font-semibold text-[#111827]">{title}</p>
        {subtitle && <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>}
      </div>
      {showChevron && (
        <ChevronRight className="w-5 h-5 text-[#6B7280] group-hover:text-[#FF6A2A] transition-colors" />
      )}
    </button>
  );
};

export const AccountMenuScreen = () => {
  // Usar hooks en lugar de props
  const { isLoggedIn, logout } = useAuth();
  const { 
    goBack,
    goToProfile,
    goToMyTickets,
    goToSettings,
    goToHome,
    // goToMyTrips,
    goToLogin
  } = useNavigation();

  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    logout?.();
  };

  function goToMyTrips(): void {
    throw new Error('Function not implemented. provisional');
  }

  return (
    <>
      <AccountLayout
        title="Cuenta"
        subtitle="Gestiona tu perfil, boletos y configuración"
        onBack={goBack}
        activePage="account"
        onHomeClick={goToHome}
        onMyTripsClick={goToMyTrips}
        onAccountClick={() => {}} // Ya estamos en cuenta
        onProfileClick={goToProfile}
        onMyTicketsClick={goToMyTickets}
        onSettingsClick={goToSettings}
        onLogoutClick={handleLogoutClick}
        isLoggedIn={isLoggedIn}
        onLoginClick={goToLogin}
        showBottomNav={true}
        showMobileHeader={true}
      >
        {!isLoggedIn && goToLogin ? (
          <LoginRequiredCard onLoginClick={goToLogin} />
        ) : (
          <div className="space-y-4">
            {/* User Profile Card */}
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 shadow-sm">
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF6A2A] to-[#C2410C] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-2xl">A</span>
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-bold text-[#111827] text-lg truncate">Ana María García</h2>
<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-semibold">
  <CheckCircle className="w-3.5 h-3.5" />
  Verificado
</span>

                  </div>
                  <div className="flex items-center gap-1 text-sm text-[#6B7280] mb-0.5">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate">ana.garcia@example.com</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-[#6B7280]">
                    <Phone className="w-3.5 h-3.5" />
                    <span>+52 55 1234 5678</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Options */}
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm overflow-hidden divide-y divide-[#E5E7EB]">
              <MenuItem
                icon={<User className="w-5 h-5" />}
                title="Mi perfil"
                subtitle="Edita tus datos y pasajeros guardados"
                onClick={goToProfile}
              />
              <MenuItem
                icon={<Ticket className="w-5 h-5" />}
                title="Mis boletos"
                subtitle="Consulta tus viajes activos y pasados"
                onClick={goToMyTickets}
              />
              <MenuItem
                icon={<Settings className="w-5 h-5" />}
                title="Configuración"
                subtitle="Seguridad, preferencias y ayuda"
                onClick={goToSettings}
              />
            </div>

            {/* Logout Button */}
            <div className="pt-2">
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-[#DC2626] text-[#DC2626] font-semibold rounded-[12px] hover:bg-[#FEF2F2] transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </AccountLayout>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onConfirm={handleConfirmLogout}
        onClose={() => setLogoutModalOpen(false)}
      />
    </>
  );
};

export default AccountMenuScreen;